import { useCallback, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { useToast } from '@/components/ui';
import { createOrgAssignment, OrgAdminApiError } from '@/api/orgAdmin';
import type { KeycloakAccountRow } from './types';

/**
 * EAA-1820 (frame 84, "Gán Level", triggered from the realm `/console/accounts`
 * row kebab — frame 83): grants a NEW đơn vị + Level to an identity that already
 * exists, from the realm list rather than the org-scoped one.
 *
 * WHY THIS IS A CREATE, NOT A "REPLACE THIS ROW'S LEVEL". A realm row
 * (`KeycloakAccountRow`) is a Keycloak IDENTITY, not an `Assignment` — it may
 * hold zero, one or several active grants across different đơn vị
 * (`row.placement` shows only the first, plus a count of the rest), and the
 * list endpoint this screen reads (`GET /accounts`) carries no assignment id to
 * act on. So there is nothing here to "replace" — the dialog always POSTs
 * `organizations/{orgId}/assignments`, the same endpoint the org-scoped
 * screen's "+ Tài khoản" and the hierarchy tree's "Cấp quyền" already use, with
 * `user_sub`/`user_email` pre-filled from the row. Moving or changing an
 * EXISTING grant is `ScopedAccountsTable`'s per-row "Chuyển đơn vị" (frame 85) —
 * that one legitimately needs an assignment id, which only the org-scoped list
 * carries per row.
 *
 * PERMISSION IS RE-CHECKED SERVER-SIDE PER ORG, not upfront here. Unlike the
 * org-scoped screen (one acting org, one `org:{id}.manage` check), this dialog
 * lets the operator pick ANY đơn vị from the realm list, so there is no single
 * capability string to gate the menu item on before submission — the screen
 * shows the item behind `canIssue` (the coarsest proxy already gating the whole
 * realm accounts surface) and lets `AUTH_PERMISSION_DENIED` surface honestly if
 * the chosen đơn vị turns out to be one this operator does not manage.
 */
export function useAssignLevelToAccount(invalidate: () => void) {
    const { t } = useTranslation();
    const config = useRuntimeConfig();
    const { accessToken } = useAuth();
    const toast = useToast();

    const [target, setTarget] = useState<KeycloakAccountRow | null>(null);
    const [orgId, setOrgId] = useState('');
    const [levelId, setLevelId] = useState('');
    const [error, setError] = useState<string | null>(null);

    const open = useCallback((row: KeycloakAccountRow) => {
        setTarget(row);
        setOrgId('');
        setLevelId('');
        setError(null);
    }, []);
    const close = useCallback(() => setTarget(null), []);

    const mutation = useMutation({
        mutationFn: () =>
            createOrgAssignment(config.apiBase, accessToken as string, orgId, {
                user_sub: target?.sub as string,
                ...(target?.email ? { user_email: target.email } : {}),
                level_id: levelId,
            }),
        onSuccess: () => {
            setTarget(null);
            invalidate();
            toast.success(
                t('console.accounts.assignLevel.success', { identity: target?.email || target?.username })
            );
        },
        onError: (err: unknown) => {
            setError(err instanceof OrgAdminApiError ? err.message : t('console.accounts.assignLevel.errorFallback'));
        },
    });

    const submit = useCallback(() => {
        if (!orgId || !levelId || !target) return;
        setError(null);
        mutation.mutate();
    }, [orgId, levelId, target, mutation]);

    return {
        target,
        open,
        close,
        orgId,
        setOrgId: (next: string) => {
            setOrgId(next);
            setLevelId(''); // EAA-1820: a Level belongs to exactly one org — changing the org invalidates the pick.
        },
        levelId,
        setLevelId,
        error,
        submit,
        busy: mutation.isPending,
    };
}

export type AssignLevelToAccountVM = ReturnType<typeof useAssignLevelToAccount>;
